import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Alert, Spinner } from 'react-bootstrap';
import { API_URL } from '../../../utils/constants';
import './GuestsFindScreen.css';

function GuestsFindScreen() {
  const [searchCriteria, setSearchCriteria] = useState({
    guestNumber: '',
    firstName: '',
    lastName: '',
    email: '',
    guestType: '',
    active: 'all'
  });
  
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [guestTypes, setGuestTypes] = useState([]);

  useEffect(() => {
    fetchGuestTypes();
  }, []);

  const fetchGuestTypes = async () => {
    try {
      const response = await fetch(`${API_URL}/guest-types`);
      if (response.ok) {
        const data = await response.json();
        setGuestTypes(data);
      }
    } catch (error) {
      console.error('Error fetching guest types:', error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Build search query
      const searchTerms = [];
      if (searchCriteria.guestNumber) searchTerms.push(searchCriteria.guestNumber);
      if (searchCriteria.firstName) searchTerms.push(searchCriteria.firstName);
      if (searchCriteria.lastName) searchTerms.push(searchCriteria.lastName);
      if (searchCriteria.email) searchTerms.push(searchCriteria.email);

      if (searchTerms.length === 0) {
        setError('Please enter at least one search criteria');
        setLoading(false);
        return;
      }

      // Use the search endpoint for better results
      const searchQuery = searchTerms.join(' ');
      const response = await fetch(`${API_URL}/guests/search/${encodeURIComponent(searchQuery)}?limit=50`);
      
      if (response.ok) {
        let results = await response.json();
        
        // Apply additional filters
        if (searchCriteria.guestType) {
          results = results.filter(guest => 
            guest.guesttype.toLowerCase().includes(searchCriteria.guestType.toLowerCase())
          );
        }
        
        if (searchCriteria.active !== 'all') {
          const activeFilter = searchCriteria.active === 'true';
          results = results.filter(guest => guest.active === activeFilter);
        }
        
        setSearchResults(results);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Error searching guests');
      }
    } catch (error) {
      console.error('Error searching guests:', error);
      setError('Error searching guests. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchCriteria(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearSearch = () => {
    setSearchCriteria({
      guestNumber: '',
      firstName: '',
      lastName: '',
      email: '',
      guestType: '',
      active: 'all'
    });
    setSearchResults([]);
    setError('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Container fluid className="guests-find-screen">
      <Row>
        <Col>
          <h1 className="mb-4">Find Guests</h1>
          
          {/* Search Form */}
          <Card className="mb-4">
            <Card.Header>
              <h5>Search Criteria</h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSearch}>
                <Row>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Guest Number</Form.Label>
                      <Form.Control
                        type="text"
                        name="guestNumber"
                        value={searchCriteria.guestNumber}
                        onChange={handleInputChange}
                        placeholder="Enter guest number"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>First Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="firstName"
                        value={searchCriteria.firstName}
                        onChange={handleInputChange}
                        placeholder="Enter first name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Last Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="lastName"
                        value={searchCriteria.lastName}
                        onChange={handleInputChange}
                        placeholder="Enter last name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={searchCriteria.email}
                        onChange={handleInputChange}
                        placeholder="Enter email"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Guest Type</Form.Label>
                      <Form.Control
                        as="select"
                        name="guestType"
                        value={searchCriteria.guestType}
                        onChange={handleInputChange}
                      >
                        <option value="">All Types</option>
                        {guestTypes.map(type => (
                          <option key={type.id} value={type.name}>
                            {type.name}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Status</Form.Label>
                      <Form.Control
                        as="select"
                        name="active"
                        value={searchCriteria.active}
                        onChange={handleInputChange}
                      >
                        <option value="all">All Statuses</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </Form.Control>
                    </Form.Group>
                  </Col>
                  <Col md={6} className="d-flex align-items-end">
                    <div className="d-flex gap-2">
                      <Button type="submit" variant="primary" disabled={loading}>
                        {loading ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Searching...
                          </>
                        ) : (
                          'Search'
                        )}
                      </Button>
                      <Button type="button" variant="secondary" onClick={clearSearch}>
                        Clear
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>

          {/* Error Alert */}
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Search Results */}
          {searchResults.length > 0 && (
            <Card>
              <Card.Header>
                <h5>Search Results ({searchResults.length} guests found)</h5>
              </Card.Header>
              <Card.Body>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Guest #</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map(guest => (
                      <tr key={guest.id}>
                        <td>{guest.guestnumber}</td>
                        <td>{guest.firstname} {guest.lastname}</td>
                        <td>{guest.email}</td>
                        <td>{guest.guesttype}</td>
                        <td>
                          <span className={`badge ${guest.active ? 'bg-success' : 'bg-secondary'}`}>
                            {guest.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>{formatDate(guest.createdate)}</td>
                        <td>
                          <Button size="sm" variant="outline-primary" className="me-2">
                            View
                          </Button>
                          <Button size="sm" variant="outline-warning" className="me-2">
                            Edit
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}

          {/* No Results Message */}
          {searchResults.length === 0 && !loading && !error && (
            <Card>
              <Card.Body className="text-center text-muted">
                <p>Enter search criteria and click Search to find guests</p>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </Container>
  );
}

export default GuestsFindScreen;
